// Smart Tools Browser — the full-catalog browser behind the Smart drawer's
// "Browse all tools" button (owner directive 2026-07-23: "a full smart tools
// browser with categorization, tagging etc, because 220 tools is far too much
// to add to the left hand drawer"). A large modal, StylesExplorerModal-class
// chrome, portaled to document.body.
//
// LAYOUT: LEFT RAIL = Featured + All + every DOMAIN present in the data (with
// counts; future-proof — renders whatever domains exist, so a new recipe in a
// new domain needs no change here). TOP = debounced search (name + description
// + tags) with removable active tag-chip filters. BODY = responsive grid of
// REAL thumbnails (renderSmartObjectThumb, IntersectionObserver-lazy so 220
// tools scroll smoothly) + name + a hover description; each tile shows up to 3
// clickable TAG chips that add to the filter.
//
// TILE CLICK → close the browser → hand off to SM-A's per-tool modal
// openSmartToolModal({recipeId, mode:'insert'}). SM-A lands concurrently, so the
// controller is imported LAZILY and optional-chained; if it is absent we fall
// back to a direct editor.addSmartObject(id) (the drawer's own insert path).
// Integration seam = the module path './smartToolModalController' below.
//
// Keyboard: Esc closes (shared editor Esc stack); "/" focuses search.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { listRecipes } from '../../../posterTools';
import { renderSmartObjectThumb } from './smartObjectPreview';
// Shared capture-phase Esc stack — the SAME one PaintPickerModal / Texture /
// ObjectSwap push onto, so a modal opened OVER this browser closes first.
import { pushEscHandler, popEscHandler } from './inspector/text/StylesExplorerModal';
import { registerSmartToolsBrowserHost } from './smartToolsBrowserController';
import {
  ALL_DOMAIN,
  FEATURED_DOMAIN,
  browsableRecipes,
  buildDomainRail,
  domainLabel,
  recipeDomain,
} from './smartToolsTaxonomy';
import './SmartToolsBrowser.css';

const THUMB_PX = 160; // device px; downscaled into the ~150px tile art box
const SEARCH_DEBOUNCE_MS = 220;
const TILE_TAG_CAP = 3; // tag chips shown per tile (rest are searchable)

/** requestIdleCallback with a setTimeout(16) fallback — thumb renders ride idle
 *  slices so grid scroll stays smooth on weak classroom laptops. */
const scheduleIdle = (cb) => (
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback(cb, { timeout: 200 })
    : setTimeout(cb, 16)
);

export default function SmartToolsBrowser({ editor, initialDomain, onClose }) {
  // ---- catalog (registry is populated by importing posterTools above) ------
  const allBrowsable = useMemo(() => browsableRecipes(listRecipes()), []);
  const byId = useMemo(() => new Map(allBrowsable.map((r) => [r.id, r])), [allBrowsable]);
  const rail = useMemo(() => buildDomainRail(allBrowsable), [allBrowsable]);
  const featuredCount = useMemo(() => allBrowsable.filter((r) => r.featured).length, [allBrowsable]);

  const [domain, setDomain] = useState(initialDomain || FEATURED_DOMAIN);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeTags, setActiveTags] = useState([]);
  const [thumbs, setThumbs] = useState({}); // recipeId → blob URL

  // ---- Esc closes only the top-most modal (shared stack) -------------------
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const handler = () => closeRef.current?.();
    pushEscHandler(handler);
    return () => popEscHandler(handler);
  }, []);

  // ---- "/" focuses search (unless already typing in it) --------------------
  const searchRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ---- debounce the search box --------------------------------------------
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query]);

  // ---- filtering: search OVERRIDES the rail (matches StylesExplorer); active
  //      tag chips compose on top of whichever base set is in play -----------
  const q = debounced.trim().toLowerCase();
  const shown = useMemo(() => {
    let base;
    if (q) base = allBrowsable; // a query searches across ALL domains
    else if (domain === FEATURED_DOMAIN) base = allBrowsable.filter((r) => r.featured);
    else if (domain === ALL_DOMAIN) base = allBrowsable;
    else base = allBrowsable.filter((r) => recipeDomain(r) === domain);

    const matchesQuery = (r) => (
      !q
      || r.name.toLowerCase().includes(q)
      || (r.description || '').toLowerCase().includes(q)
      || (r.tags || []).some((t) => t.toLowerCase().includes(q))
    );
    const matchesTags = (r) => activeTags.every((t) => (r.tags || []).includes(t));
    return base.filter((r) => matchesQuery(r) && matchesTags(r));
  }, [q, domain, activeTags, allBrowsable]);

  // ---- lazy thumb machinery (IntersectionObserver + idle pump) -------------
  const thumbsRef = useRef(thumbs);
  thumbsRef.current = thumbs;
  const queuedRef = useRef(new Set()); // recipeIds queued or in flight
  const queueRef = useRef([]);
  const pumpingRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true; // re-arm (StrictMode runs cleanup but keeps refs)
    return () => { aliveRef.current = false; };
  }, []);

  const pump = useCallback(() => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    const step = (deadline) => {
      if (!aliveRef.current) { pumpingRef.current = false; return; }
      const id = queueRef.current.shift();
      if (!id) { pumpingRef.current = false; return; }
      renderSmartObjectThumb({ recipeId: id, size: THUMB_PX })
        .then((url) => { if (aliveRef.current && url) setThumbs((m) => ({ ...m, [id]: url })); })
        .catch(() => { queuedRef.current.delete(id); /* allow a retry on re-scroll */ })
        .finally(() => {
          if (deadline && typeof deadline.timeRemaining === 'function' && deadline.timeRemaining() > 4) {
            step(deadline); // same idle slice, room left (a thumb is a few ms)
          } else {
            scheduleIdle(step);
          }
        });
    };
    scheduleIdle(step);
  }, []);

  const enqueueThumb = useCallback((id) => {
    if (!id || thumbsRef.current[id] || queuedRef.current.has(id)) return;
    queuedRef.current.add(id);
    queueRef.current.push(id);
    pump();
  }, [pump]);

  const gridRef = useRef(null);
  const ioRef = useRef(null);
  const nodesRef = useRef(new Map()); // recipeId → element

  const registerCard = useCallback((node) => {
    if (!node) return;
    const id = node.getAttribute('data-recipe-id');
    if (!id) return;
    nodesRef.current.set(id, node);
    if (ioRef.current) ioRef.current.observe(node);
    else if (typeof IntersectionObserver === 'undefined') enqueueThumb(id);
  }, [enqueueThumb]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (!en.isIntersecting) continue;
        const id = en.target.getAttribute('data-recipe-id');
        if (id) enqueueThumb(id);
      }
    }, { root: gridRef.current, rootMargin: '200px 0px' });
    ioRef.current = io;
    nodesRef.current.forEach((node) => io.observe(node));
    return () => { io.disconnect(); ioRef.current = null; };
  }, [enqueueThumb]);

  // ---- interactions --------------------------------------------------------
  const toggleTag = useCallback((tag) => {
    if (!tag) return;
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  }, []);

  const pickDomain = (id) => { setDomain(id); setQuery(''); setDebounced(''); };

  // Tile click → close, then hand off to SM-A's per-tool insert modal (lazy +
  // optional); fall back to a direct insert if that controller isn't present.
  const handlePick = useCallback((recipeId) => {
    const recipe = byId.get(recipeId);
    if (!recipe) return;
    onClose();
    const fallback = () => {
      try {
        const p = editor?.addSmartObject?.(recipeId);
        if (p && typeof p.catch === 'function') p.catch((e) => console.error('[smartToolsBrowser] insert failed', e));
      } catch (e) { console.error('[smartToolsBrowser] insert failed', e); }
    };
    import('./smartToolModalController')
      .then((mod) => {
        if (mod && typeof mod.openSmartToolModal === 'function') {
          mod.openSmartToolModal({ recipeId, mode: 'insert' });
        } else {
          fallback();
        }
      })
      .catch(fallback); // SM-A controller not landed yet → direct insert
  }, [byId, editor, onClose]);

  // Grid delegation: a click on a tag chip filters; a click anywhere else on a
  // tile inserts. Keyboard mirrors it (Enter / Space on a focused tile or chip).
  const onGridClick = (e) => {
    const chip = e.target.closest?.('[data-tag]');
    if (chip) { toggleTag(chip.getAttribute('data-tag')); return; }
    const tile = e.target.closest?.('[data-recipe-id]');
    if (tile) handlePick(tile.getAttribute('data-recipe-id'));
  };
  const onGridKeyDown = (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const chip = e.target.closest?.('[data-tag]');
    if (chip) { e.preventDefault(); toggleTag(chip.getAttribute('data-tag')); return; }
    const tile = e.target.closest?.('[data-recipe-id]');
    if (tile) { e.preventDefault(); handlePick(tile.getAttribute('data-recipe-id')); }
  };

  const filtering = !!q || activeTags.length > 0;

  return createPortal(
    <div
      className="cce-stb-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="cce-stb" role="dialog" aria-modal="true" aria-label="Smart Tools browser">
        <div className="cce-stb-head">
          <h2 className="cce-stb-title">Smart Tools</h2>
          <input
            ref={searchRef}
            type="search"
            className="cce-stb-search"
            placeholder="Search tools by name, tag, or subject…   ( / )"
            aria-label="Search smart tools"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="button" className="cce-stb-close" aria-label="Close" onClick={onClose}>×</button>
        </div>

        {activeTags.length > 0 && (
          <div className="cce-stb-activetags">
            <span className="cce-stb-activetags-label">Tags:</span>
            {activeTags.map((t) => (
              <button
                key={t}
                type="button"
                className="cce-stb-chip active"
                onClick={() => toggleTag(t)}
                title={`Remove “${t}” filter`}
              >
                {t} <span aria-hidden="true">×</span>
              </button>
            ))}
            <button type="button" className="cce-stb-clear" onClick={() => setActiveTags([])}>
              Clear tags
            </button>
          </div>
        )}

        <div className="cce-stb-main">
          <div className="cce-stb-rail">
            <button
              type="button"
              className={`cce-stb-cat${!q && domain === FEATURED_DOMAIN ? ' active' : ''}`}
              onClick={() => pickDomain(FEATURED_DOMAIN)}
            >
              <span className="cce-stb-cat-label">★ Featured</span>
              <span className="cce-stb-cat-count">{featuredCount}</span>
            </button>
            <button
              type="button"
              className={`cce-stb-cat${!q && domain === ALL_DOMAIN ? ' active' : ''}`}
              onClick={() => pickDomain(ALL_DOMAIN)}
            >
              <span className="cce-stb-cat-label">All tools</span>
              <span className="cce-stb-cat-count">{allBrowsable.length}</span>
            </button>
            <div className="cce-stb-rail-sep" />
            {rail.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`cce-stb-cat${!q && domain === d.id ? ' active' : ''}`}
                onClick={() => pickDomain(d.id)}
              >
                <span className="cce-stb-cat-label">{d.label}</span>
                <span className="cce-stb-cat-count">{d.count}</span>
              </button>
            ))}
          </div>

          <div className="cce-stb-content">
            <div className="cce-stb-contexthdr">
              <span className="cce-stb-contexthdr-title">
                {q
                  ? `Results for “${debounced.trim()}”`
                  : domain === FEATURED_DOMAIN
                    ? 'Featured'
                    : domain === ALL_DOMAIN
                      ? 'All tools'
                      : domainLabel(domain)}
              </span>
              <span className="cce-stb-contexthdr-count">{shown.length} {shown.length === 1 ? 'tool' : 'tools'}</span>
            </div>

            {shown.length === 0 ? (
              <div className="cce-stb-empty">
                {filtering ? (
                  <>
                    <p>No tools match your search.</p>
                    <button
                      type="button"
                      className="cce-stb-empty-reset"
                      onClick={() => { setQuery(''); setDebounced(''); setActiveTags([]); }}
                    >
                      Clear search &amp; tags
                    </button>
                  </>
                ) : (
                  <p>No smart tools in this section yet.</p>
                )}
              </div>
            ) : (
              <div
                className="cce-stb-grid"
                ref={gridRef}
                onClick={onGridClick}
                onKeyDown={onGridKeyDown}
              >
                {shown.map((r) => {
                  const url = thumbs[r.id];
                  return (
                    <div
                      key={r.id}
                      className="cce-stb-tile"
                      data-recipe-id={r.id}
                      role="button"
                      tabIndex={0}
                      aria-label={`Add ${r.name}`}
                      title={`${r.name} — ${r.description}`}
                      ref={registerCard}
                    >
                      <span className="cce-stb-tile-art">
                        {url
                          ? <img className="cce-stb-tile-img" src={url} alt="" draggable={false} />
                          : <span className="cce-stb-tile-ph" aria-hidden="true">▦</span>}
                        <span className="cce-stb-tile-desc">{r.description}</span>
                      </span>
                      <span className="cce-stb-tile-name">{r.name}</span>
                      {(r.tags || []).length > 0 && (
                        <span className="cce-stb-tile-tags">
                          {(r.tags || []).slice(0, TILE_TAG_CAP).map((t) => (
                            <button
                              key={t}
                              type="button"
                              data-tag={t}
                              className={`cce-stb-chip${activeTags.includes(t) ? ' active' : ''}`}
                              title={`Filter by “${t}”`}
                            >
                              {t}
                            </button>
                          ))}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="cce-stb-footer">
          <span className="cce-stb-footer-hint">Click a tool to place it on your poster · press <kbd>/</kbd> to search · <kbd>Esc</kbd> to close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---- singleton host (paintModalController pattern) -------------------------
// Mounted once beside the editor's other modal hosts (RibbonMount). Registers
// its setter so openSmartToolsBrowser() anywhere opens the one browser; renders
// nothing until opened. The bumped _id keys a fresh mount on re-open.
export function SmartToolsBrowserHost() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => registerSmartToolsBrowserHost(setCfg), []);
  if (!cfg) return null;
  return (
    <SmartToolsBrowser
      key={cfg._id}
      editor={cfg.editor}
      initialDomain={cfg.initialDomain}
      onClose={() => setCfg(null)}
    />
  );
}
